import type { utils } from 'blockly/core';

import { buildAddonToolboxCategories } from '../addonCategories.js';
import { categoryCssConfig, categorySubCssConfig } from '../categoryCssConfig.js';
import {
  ARDUINO_COLOR,
  IO_COLOR,
  LOGIC_COLOR,
  LOOPS_COLOR,
  MATH_COLOR,
  PROCEDURE_COLOR,
  SERIAL_COLOR,
  TEXT_COLOR,
  VAR_COLOR,
} from './constants.js';

type ToolboxInfo = utils.toolbox.ToolboxInfo;

/** Advanced toolbox — full Arduino and standard Blockly categories. */
export function buildAdvancedToolbox(enabledAddonIds: readonly string[] = []): ToolboxInfo {
  const contents: utils.toolbox.ToolboxItemInfo[] = [
    {
      kind: 'category',
      name: 'Logic',
      colour: String(LOGIC_COLOR),
      cssconfig: categoryCssConfig('logic'),
      contents: [
        { kind: 'block', type: 'controls_if' },
        { kind: 'block', type: 'logic_compare' },
        { kind: 'block', type: 'logic_operation' },
        { kind: 'block', type: 'logic_negate' },
        { kind: 'block', type: 'logic_boolean' },
        { kind: 'block', type: 'logic_ternary' },
      ],
    },
    {
      kind: 'category',
      name: 'Loop',
      colour: String(LOOPS_COLOR),
      cssconfig: categoryCssConfig('loop'),
      contents: [
        { kind: 'block', type: 'controls_repeat_ext' },
        { kind: 'block', type: 'controls_whileUntil' },
        { kind: 'block', type: 'controls_for' },
        { kind: 'block', type: 'controls_flow_statements' },
      ],
    },
    {
      kind: 'category',
      name: 'Math',
      colour: String(MATH_COLOR),
      cssconfig: categoryCssConfig('math'),
      contents: [
        { kind: 'block', type: 'math_number' },
        { kind: 'block', type: 'math_arithmetic' },
        { kind: 'block', type: 'math_single' },
        { kind: 'block', type: 'math_random_int' },
        { kind: 'block', type: 'math_constrain' },
        { kind: 'block', type: 'math_modulo' },
      ],
    },
    {
      kind: 'category',
      name: 'Variables',
      colour: String(VAR_COLOR),
      cssconfig: categoryCssConfig('variables'),
      custom: 'SKYROVER_VARIABLES',
    },
    {
      kind: 'category',
      name: 'Text',
      colour: String(TEXT_COLOR),
      cssconfig: categoryCssConfig('text'),
      contents: [
        { kind: 'block', type: 'text' },
        { kind: 'block', type: 'text_char' },
        { kind: 'block', type: 'text_join' },
        { kind: 'block', type: 'text_length' },
      ],
    },
    {
      kind: 'category',
      name: 'Functions',
      colour: String(PROCEDURE_COLOR),
      cssconfig: categoryCssConfig('functions'),
      custom: 'PROCEDURE',
    },
    {
      kind: 'category',
      name: 'Arduino',
      colour: String(ARDUINO_COLOR),
      cssconfig: categoryCssConfig('arduino'),
      contents: [
        { kind: 'block', type: 'arduino_program' },
        { kind: 'block', type: 'arduino_delay' },
        { kind: 'block', type: 'arduino_delay_microseconds' },
        { kind: 'block', type: 'arduino_millis' },
        { kind: 'block', type: 'arduino_micros' },
        {
          kind: 'category',
          name: 'Digital I/O',
          colour: String(IO_COLOR),
          cssconfig: categorySubCssConfig('digital-io'),
          contents: [
            { kind: 'block', type: 'arduino_pin_mode' },
            { kind: 'block', type: 'arduino_digital_write' },
            { kind: 'block', type: 'arduino_digital_read' },
            { kind: 'block', type: 'arduino_high_low' },
          ],
        },
        {
          kind: 'category',
          name: 'Analog I/O',
          colour: String(IO_COLOR),
          cssconfig: categorySubCssConfig('analog-io'),
          contents: [
            { kind: 'block', type: 'arduino_analog_read' },
            { kind: 'block', type: 'arduino_analog_write' },
          ],
        },
        {
          kind: 'category',
          name: 'Serial',
          colour: String(SERIAL_COLOR),
          cssconfig: categorySubCssConfig('serial'),
          contents: [
            { kind: 'block', type: 'arduino_serial_begin' },
            { kind: 'block', type: 'arduino_serial_print' },
            { kind: 'block', type: 'arduino_serial_println' },
            { kind: 'block', type: 'arduino_serial_available' },
            { kind: 'block', type: 'arduino_serial_read' },
            { kind: 'block', type: 'arduino_serial_read_char' },
          ],
        },
      ],
    },
    ...buildAddonToolboxCategories(enabledAddonIds),
  ];

  return {
    kind: 'categoryToolbox',
    contents,
  };
}
